import { useState } from 'react';
import type { OverduePatrol, StillPatrol } from '../types';

/**
 * Patrols past their "due back by" time whose patroller did not answer the
 * phone's check (pt_overdue). Shown on every dashboard until the patrol ends.
 *
 * A banner, not the full-screen alarm modal: overdue usually means a patrol
 * ran long or a phone lost signal, and it must not drown out an alarm. The
 * contacts have already been emailed by the time this appears.
 */
export default function OverdueBanner({ overdue, still }: {
  overdue: Map<string, OverduePatrol>;
  /** Not moving, contacts told (pt_still, 2026-10-12). Same banner, own line. */
  still: Map<string, StillPatrol>;
}) {
  // "Seen" hides one patrol's line on this screen only; it does not clear it
  // for anyone else, and it comes back on a refresh while still overdue.
  // Keyed by kind as well: being still is a different warning from being late.
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const items = Array.from(overdue.values()).filter((o) => !seen.has(`o:${o.patrol_id}`));
  const stillItems = Array.from(still.values()).filter((s) => !seen.has(`s:${s.patrol_id}`));
  if (items.length === 0 && stillItems.length === 0) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-300 px-4 py-2 space-y-1" role="alert">
      {items.map((o) => (
        <div key={o.patrol_id} className="flex items-center gap-3 text-sm text-amber-900">
          <span className="font-semibold">⚠ {o.guard_name} is overdue</span>
          <span>
            {o.expected_end_at
              ? `- was due back by ${new Date(o.expected_end_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : ''}
            {' '}and has not answered PatrolIt&apos;s check. Their contacts have been emailed.
          </span>
          <button
            type="button"
            onClick={() => setSeen((s) => new Set(s).add(`o:${o.patrol_id}`))}
            className="ml-auto text-xs font-semibold underline"
          >
            Seen
          </button>
        </div>
      ))}
      {stillItems.map((s) => (
        <div key={`s:${s.patrol_id}`} className="flex items-center gap-3 text-sm text-amber-900">
          <span className="font-semibold">⚠ {s.guard_name} has not moved</span>
          <span>
            {s.minutes ? `for ${s.minutes} minutes` : ''} and has not answered PatrolIt&apos;s
            check - or their phone stopped reporting. Their contacts have been emailed.
          </span>
          <button
            type="button"
            onClick={() => setSeen((x) => new Set(x).add(`s:${s.patrol_id}`))}
            className="ml-auto text-xs font-semibold underline"
          >
            Seen
          </button>
        </div>
      ))}
    </div>
  );
}
